import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { supabase, type Project, type Todo } from '../lib/supabase'
import { cacheProjects, cacheTodos, getCachedProjects, getCachedTodos } from '../lib/offlineStorage'
import { getUserTeamRole, getTeamMemberCount } from '../services/teamService'
import { isNativeApp } from '../lib/platform'
import { sendLimitReachedEmail } from '../services/limitReachedService'
import { isDemoModeActive } from '../contexts/DemoContext'

interface TodoCountsByStatus {
  pending: number
  in_progress: number
  completed: number
  deleted: number
  total: number
}

type GanttFullscreenMode = 'none' | 'with-sidebar' | 'full'
type ActiveTab = 'tasks' | 'activity' | 'calendar' | 'gantt'

interface AppState {
  projects: Project[]
  todos: Todo[]
  selectedProjectId: string | null
  loading: boolean
  error: string | null
  isOffline: boolean
  isProjectSelectorOpen: boolean  // モバイルプロジェクト選択画面の開閉状態
  swipedTodoId: string | null  // 現在スワイプ状態のタスクID
  todoFilesChangedAt: number  // ファイルのリアルタイム同期用タイムスタンプ
  lastManualUpdateAt: number  // 最後に手動更新した時刻（リアルタイム同期スキップ用）
  pendingGanttOrders: Map<string, number>  // DB更新中のgantt_sort_order（todoId -> newOrder）
  ganttFullscreenMode: GanttFullscreenMode  // ガントチャートの全画面モード
  ganttDayWidth: number  // ガントチャートの1日あたりの幅（ピクセル）
  ganttCenterDayIndex: number  // ガントチャートの現在の中心日付インデックス（ズーム維持用）
  ganttSelectedTeamId: string | null  // ガントチャートで選択中のチームID（null = 全て）
  activeTab: ActiveTab  // TodoListのアクティブタブ（再マウントしても維持）
  // セクション全体の表示/非表示状態（「全プロジェクト」選択時用）
  isPersonalSectionVisible: boolean
  isTeamSectionVisible: boolean

  // ページネーション状態
  hasMoreTodos: boolean
  loadingMore: boolean
  currentPage: number
  pageSize: number
  totalTodoCount: number

  // ステータス別のタスク数（全体カウント）
  todoCountsByStatus: TodoCountsByStatus

  fetchProjects: () => Promise<void>
  fetchTodos: (projectId?: string) => Promise<void>
  fetchTodoStats: (projectId?: string) => Promise<void>
  loadMoreTodos: () => Promise<void>
  resetTodos: () => void
  addProject: (project: Omit<Project, 'id' | 'created_at' | 'updated_at' | 'user_id' | 'display_order'>) => Promise<Project | null>
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>
  updateProjectOrder: (id: string, newOrder: number) => Promise<void>
  deleteProject: (id: string) => Promise<void>

  addTodo: (todo: Omit<Todo, 'id' | 'created_at' | 'updated_at' | 'completed_at' | 'display_order' | 'user_id' | 'gantt_sort_order'>) => Promise<void>
  updateTodo: (id: string, updates: Partial<Todo>) => Promise<void>
  updateTodoOrder: (id: string, newOrder: number) => Promise<void>
  updateTodosOrderBatch: (todoIds: string[], newOrders: number[]) => Promise<void>
  updateGanttTodosOrder: (todoIds: string[], newOrders: number[]) => Promise<void>
  syncGanttOrderFromTaskOrder: (projectId: string) => Promise<void>
  syncTaskOrderFromGanttOrder: (projectId: string) => Promise<void>
  deleteTodo: (id: string) => Promise<void>

  setSelectedProjectId: (id: string | null) => Promise<void>
  setIsOffline: (offline: boolean) => void
  setIsProjectSelectorOpen: (isOpen: boolean) => void
  setSwipedTodoId: (id: string | null) => void
  setGanttFullscreenMode: (mode: GanttFullscreenMode) => void
  setGanttDayWidth: (width: number) => void
  setGanttCenterDayIndex: (index: number) => void
  setGanttSelectedTeamId: (teamId: string | null) => void
  setActiveTab: (tab: ActiveTab) => void
  setIsPersonalSectionVisible: (visible: boolean) => void
  setIsTeamSectionVisible: (visible: boolean) => void
  notifyTodoFilesChanged: () => void
  clearError: () => void
  clearStore: () => void
  getLastManualUpdateAt: () => number

  // デモモード用のセッター
  setProjects: (projects: Project[]) => void
  setTodos: (todos: Todo[]) => void
  setSelectedProjectIdForDemo: (id: string | null) => void  // DB呼び出しなしでプロジェクト選択
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
  projects: [],
  todos: [],
  selectedProjectId: null,
  loading: false,
  error: null,
  isOffline: false,
  isProjectSelectorOpen: false,
  swipedTodoId: null,
  todoFilesChangedAt: 0,
  lastManualUpdateAt: 0,
  pendingGanttOrders: new Map(),
  ganttFullscreenMode: 'none',
  ganttDayWidth: 32, // デフォルト32px
  ganttCenterDayIndex: 365, // 今日を中心（DAYS_BEFORE_TODAY = 365）
  ganttSelectedTeamId: null, // 全チーム表示
  // activeTabはlocalStorageのdefaultViewから初期化
  activeTab: (() => {
    try {
      const saved = localStorage.getItem('defaultView') as ActiveTab | null
      if (saved && ['tasks', 'calendar', 'gantt'].includes(saved)) {
        return saved
      }
    } catch {
      // ignore
    }
    return 'tasks'
  })(),
  // セクション全体の表示/非表示状態（localStorageから復元）
  isPersonalSectionVisible: (() => {
    try {
      const saved = localStorage.getItem('personal_section_visible')
      return saved !== null ? JSON.parse(saved) : true
    } catch {
      return true
    }
  })(),
  isTeamSectionVisible: (() => {
    try {
      const saved = localStorage.getItem('team_section_visible')
      return saved !== null ? JSON.parse(saved) : true
    } catch {
      return true
    }
  })(),

  // ページネーション初期値
  hasMoreTodos: true,
  loadingMore: false,
  currentPage: 0,
  pageSize: 50,
  totalTodoCount: 0,

  // ステータス別タスク数の初期値
  todoCountsByStatus: {
    pending: 0,
    in_progress: 0,
    completed: 0,
    deleted: 0,
    total: 0
  },

  fetchProjects: async () => {
    // デモモード中はDB呼び出しをスキップ
    if (isDemoModeActive()) {
      set({ loading: false })
      return
    }

    set({ loading: true, error: null })
    try {
      // プロジェクトとチーム情報を取得
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          teams:team_id (
            id,
            name,
            description,
            owner_id
          )
        `)
        .order('display_order', { ascending: true })

      if (projectsError) throw projectsError

      // 現在のユーザーIDを取得
      const { data: { user } } = await supabase.auth.getUser()
      const currentUserId = user?.id

      // チームプロジェクトの場合、自分のロールとメンバー数を取得
      const projectsWithRole = await Promise.all((projectsData || []).map(async (project) => {
        // team_idが設定されているがteamsがnullの場合（削除されたチーム）はクリーンアップ
        if (project.team_id && !project.teams) {
          console.warn(`Project ${project.id} has invalid team_id ${project.team_id}. Cleaning up...`)
          // プロジェクトをチーム設定解除（個人プロジェクトに戻す）
          await supabase
            .from('projects')
            .update({
              is_team_project: false,
              team_id: null
            })
            .eq('id', project.id)

          // 更新後のプロジェクト情報を返す
          return {
            ...project,
            is_team_project: false,
            team_id: null,
            team: null,
            team_role: undefined
          }
        }

        // チームプロジェクトの場合、個人設定を取得
        let userShowInAllProjects = true // デフォルト値
        if (project.is_team_project && currentUserId) {
          const { data: settingData } = await supabase
            .from('user_project_settings')
            .select('show_in_all_projects')
            .eq('user_id', currentUserId)
            .eq('project_id', project.id)
            .maybeSingle()

          if (settingData) {
            userShowInAllProjects = settingData.show_in_all_projects
          }
        }

        if (project.team_id && currentUserId) {
          // RPC関数でチームロールとメンバー数を取得
          const [userRole, memberCount] = await Promise.all([
            getUserTeamRole(project.team_id, currentUserId).catch(() => null),
            getTeamMemberCount(project.team_id).catch(() => 0)
          ])

          return {
            ...project,
            team: project.teams || null,
            team_role: userRole || undefined,
            team_member_count: memberCount,
            user_show_in_all_projects: userShowInAllProjects
          }
        }
        return {
          ...project,
          team: project.teams || null,
          team_role: undefined,
          user_show_in_all_projects: userShowInAllProjects
        }
      }))

      // データ取得成功時にキャッシュに保存
      if (projectsWithRole) {
        await cacheProjects(projectsWithRole)
      }

      set({ projects: projectsWithRole || [], loading: false, isOffline: false })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('fetchProjects error:', error)

      // ネットワークエラーの場合はオフラインモードに
      if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
        // キャッシュから取得を試みる
        const cachedProjects = await getCachedProjects()

        if (cachedProjects && cachedProjects.length > 0) {
          // キャッシュデータがある場合はそれを使用
          set({
            projects: cachedProjects,
            isOffline: true,
            loading: false,
            error: null  // エラーメッセージは表示しない（ネットワークバナーがあるため）
          })
        } else {
          set({ error: 'オフラインです。データがキャッシュされていません', loading: false, isOffline: true, projects: [] })
        }
      } else {
        set({ error: errorMessage, loading: false })
      }
    }
  },

  fetchTodos: async (projectId?: string) => {
    // デモモード中はDB呼び出しをスキップ
    if (isDemoModeActive()) {
      set({ loading: false })
      return
    }

    set({ loading: true, error: null, currentPage: 0, hasMoreTodos: true })
    try {
      const { pageSize, projects } = get()
      let query = supabase
        .from('todos')
        .select('*, project:projects(*,teams(*)), thumbnail_image:todo_images!thumbnail_image_id(*), assigned_user:user_profiles!assigned_to_user_id(id, nickname, avatar_url)', { count: 'exact' })
        .order('display_order', { ascending: true })
        .range(0, pageSize - 1)  // 最初の50件

      if (projectId) {
        query = query.eq('project_id', projectId)
      } else {
        // 「全てのプロジェクト」選択時は、show_in_all_projectsがtrueのプロジェクトのみ表示
        const visibleProjectIds = projects
          .filter(p => {
            // 個人プロジェクト: show_in_all_projectsを使用
            // チームプロジェクト: user_show_in_all_projectsを使用
            return p.is_team_project
              ? (p.user_show_in_all_projects !== undefined ? p.user_show_in_all_projects : true)
              : p.show_in_all_projects
          })
          .map(p => p.id)

        if (visibleProjectIds.length > 0) {
          query = query.in('project_id', visibleProjectIds)
        } else {
          // 表示可能なプロジェクトがない場合は空配列を返す
          set({
            todos: [],
            loading: false,
            currentPage: 0,
            hasMoreTodos: false,
            totalTodoCount: 0,
            isOffline: false
          })
          return
        }
      }

      const { data, error, count } = await query

      if (error) throw error

      // データ取得成功時にキャッシュに保存
      if (data) {
        await cacheTodos(data)
      }

      const hasMore = count ? count > pageSize : false

      // 保留中のgantt_sort_order更新を再適用
      const pendingOrders = get().pendingGanttOrders
      let todosWithPendingOrders = data || []
      if (pendingOrders.size > 0) {
        todosWithPendingOrders = todosWithPendingOrders.map(t => {
          const pendingOrder = pendingOrders.get(t.id)
          if (pendingOrder !== undefined) {
            return { ...t, gantt_sort_order: pendingOrder }
          }
          return t
        })
      }

      set({
        todos: todosWithPendingOrders,
        loading: false,
        currentPage: 0,
        hasMoreTodos: hasMore,
        totalTodoCount: count || 0,
        isOffline: false
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('fetchTodos error:', error)

      // ネットワークエラーの場合はオフラインモードに
      if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
        // キャッシュから取得を試みる
        const cachedTodos = await getCachedTodos()

        if (cachedTodos && cachedTodos.length > 0) {
          // キャッシュデータがある場合はそれを使用
          set({
            todos: cachedTodos,
            isOffline: true,
            loading: false,
            hasMoreTodos: false,
            error: null  // エラーメッセージは表示しない（ネットワークバナーがあるため）
          })
        } else {
          set({ error: 'オフラインです。データがキャッシュされていません', loading: false, hasMoreTodos: false, isOffline: true, todos: [] })
        }
      } else {
        set({ error: errorMessage, loading: false, hasMoreTodos: false, todos: [] })
      }
    }
  },

  fetchTodoStats: async (projectId?: string) => {
    // デモモード中はDB呼び出しをスキップ
    if (isDemoModeActive()) {
      return
    }

    try {
      const { projects } = get()
      // 各ステータスのカウントを並列で取得
      const statusTypes: Array<'pending' | 'in_progress' | 'completed' | 'deleted'> = ['pending', 'in_progress', 'completed', 'deleted']

      const countPromises = statusTypes.map(async (status) => {
        let query = supabase
          .from('todos')
          .select('*', { count: 'exact', head: true })
          .eq('status', status)

        if (projectId) {
          query = query.eq('project_id', projectId)
        } else {
          // 「全てのプロジェクト」選択時は、show_in_all_projectsがtrueのプロジェクトのみカウント
          const visibleProjectIds = projects
            .filter(p => p.show_in_all_projects)
            .map(p => p.id)

          if (visibleProjectIds.length > 0) {
            query = query.in('project_id', visibleProjectIds)
          } else {
            return 0
          }
        }

        const { count, error } = await query

        if (error) {
          console.error(`Error fetching ${status} count:`, error)
          return 0
        }

        return count || 0
      })

      const counts = await Promise.all(countPromises)

      const todoCountsByStatus = {
        pending: counts[0],
        in_progress: counts[1],
        completed: counts[2],
        deleted: counts[3],
        total: counts[0] + counts[1] + counts[2] // 削除済みは除外
      }

      set({ todoCountsByStatus })
    } catch (error) {
      console.error('fetchTodoStats error:', error)
    }
  },

  loadMoreTodos: async () => {
    // デモモード中はDB呼び出しをスキップ
    if (isDemoModeActive()) {
      return
    }

    const { loadingMore, hasMoreTodos, selectedProjectId, currentPage, pageSize, todos, totalTodoCount, projects } = get()

    if (loadingMore || !hasMoreTodos) {
      return
    }

    // すでに全データを読み込んでいる場合はスキップ
    if (todos.length >= totalTodoCount && totalTodoCount > 0) {
      set({ hasMoreTodos: false })
      return
    }

    set({ loadingMore: true, error: null })
    try {
      const nextPage = currentPage + 1
      const start = nextPage * pageSize
      const end = start + pageSize - 1

      let query = supabase
        .from('todos')
        .select('*, project:projects(*,teams(*)), thumbnail_image:todo_images!thumbnail_image_id(*), assigned_user:user_profiles!assigned_to_user_id(id, nickname, avatar_url)', { count: 'exact' })
        .order('display_order', { ascending: true })
        .range(start, end)

      if (selectedProjectId) {
        query = query.eq('project_id', selectedProjectId)
      } else {
        // 「全てのプロジェクト」選択時は、show_in_all_projectsがtrueのプロジェクトのみ表示
        const visibleProjectIds = projects
          .filter(p => p.show_in_all_projects)
          .map(p => p.id)

        if (visibleProjectIds.length > 0) {
          query = query.in('project_id', visibleProjectIds)
        } else {
          set({ loadingMore: false, hasMoreTodos: false })
          return
        }
      }

      const { data, error, count } = await query

      // エラーオブジェクトが不正な場合は特別な処理（エラーログ前にチェック）
      if (error && typeof error === 'object' && error.message === '{"') {
        // Supabaseの既知の問題：データの終わりで不正なJSONが返されることがある
        set({ loadingMore: false, hasMoreTodos: false })
        return
      }

      if (error) {
        console.error('loadMoreTodos Supabase error:', error)
        throw error
      }

      // データが空の場合は、もうデータがないと判断
      if (!data || data.length === 0) {
        set({ loadingMore: false, hasMoreTodos: false })
        return
      }

      const hasMore = count ? count > (nextPage + 1) * pageSize : false
      set({
        todos: [...todos, ...(data || [])],
        loadingMore: false,
        currentPage: nextPage,
        hasMoreTodos: hasMore
      })
    } catch (error) {
      // まずエラーメッセージを取得
      let errorMessage = 'Unknown error occurred'
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String((error as any).message)
      } else {
        errorMessage = String(error)
      }

      // エラーメッセージが不正な場合は、ログを最小限にして無視
      if (errorMessage === '{"' || errorMessage.trim() === '' || errorMessage === '{}') {
        console.warn('Ignoring malformed pagination error')
        // エラーを設定せず、ただ読み込みを停止
        set({ loadingMore: false, hasMoreTodos: false })
        return
      }

      // 正当なエラーの場合のみ詳細ログを出力
      console.error('loadMoreTodos error - full object:', error)
      console.error('loadMoreTodos error - JSON.stringify:', JSON.stringify(error, null, 2))
      console.error('loadMoreTodos error - type:', typeof error)
      console.error('loadMoreTodos error - keys:', error ? Object.keys(error) : 'null')

      // Supabaseエラーの詳細を確認
      if (error && typeof error === 'object') {
        const errObj = error as any
        console.error('loadMoreTodos - hint:', errObj.hint)
        console.error('loadMoreTodos - details:', errObj.details)
        console.error('loadMoreTodos - code:', errObj.code)
      }

      console.error('loadMoreTodos error message:', errorMessage)
      set({ error: errorMessage, loadingMore: false, hasMoreTodos: false })
    }
  },

  resetTodos: () => {
    set({
      todos: [],
      currentPage: 0,
      hasMoreTodos: true
    })
  },

  addProject: async (project) => {
    set({ loading: true, error: null })
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      // 全プロジェクトの中から最小display_orderを取得
      const { data: minOrderData } = await supabase
        .from('projects')
        .select('display_order')
        .eq('user_id', user.id)
        .order('display_order', { ascending: true })
        .limit(1)
        .maybeSingle()

      const minOrder = minOrderData?.display_order ?? 0
      const newOrder = minOrder - 1

      const { data, error } = await supabase
        .from('projects')
        .insert([{ ...project, user_id: user.id, display_order: newOrder }])
        .select()
        .single()

      if (error) throw error
      set(state => ({
        projects: [data, ...state.projects],
        loading: false
      }))
      return data
    } catch (error) {
      const errorMessage = (error as Error).message

      // RLS policy violation for projects
      if (errorMessage.includes('row-level security policy') || errorMessage.includes('violates') || errorMessage.includes('new row violates')) {
        // ユーザーのプラン情報を取得してメッセージをカスタマイズ
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('subscription_tier')
            .eq('id', user.id)
            .single()

          const { data: configData } = await supabase
            .from('admin_tier_config')
            .select('max_projects')
            .eq('tier_name', profileData?.subscription_tier || 'free')
            .eq('is_active', true)
            .single()

          const maxProjects = configData?.max_projects || 2
          const tierName = profileData?.subscription_tier === 'free' ? 'Freeプラン' :
                          profileData?.subscription_tier === 'pro' ? 'Proプラン' :
                          profileData?.subscription_tier === 'premium' ? 'Premiumプラン' : 'AIアシスタントプラン'

          // ネイティブアプリの場合は、課金誘導なしのメッセージ + メール送信
          if (isNativeApp()) {
            // バックグラウンドでメール送信（エラーは無視）
            sendLimitReachedEmail({
              limitType: 'projects',
              maxAllowed: maxProjects
            }).catch(console.error)

            set({
              error: `無料プランの上限に達しました\n\n無料プランでは、作成できるプロジェクトは${maxProjects}つまでです。\nこれ以上プロジェクトを作成するには、TaStacKの有料プランが必要です。\n\nすでに有料プランをご利用の方は、ログインすると制限が解除されます。\n\n※詳細をメールでお送りしました。`,
              loading: false
            })
          } else {
            // Webの場合は従来のメッセージ
            set({
              error: `プロジェクトを作成できません。${tierName}では${maxProjects}個までしか作成できません。既存のプロジェクトを削除するか、プランをアップグレードしてください。`,
              loading: false
            })
          }
        } else {
          set({
            error: 'プロジェクトの作成上限に達しました。既存のプロジェクトを削除するか、プランをアップグレードしてください。',
            loading: false
          })
        }
      } else {
        set({ error: errorMessage, loading: false })
      }
      return null
    }
  },

  updateProject: async (id, updates) => {
    // 楽観的更新（即座にUIを更新）
    const oldProject = get().projects.find(p => p.id === id)
    if (oldProject) {
      const updatedProject = { ...oldProject, ...updates }
      // show_in_all_projectsはチームプロジェクトの場合user_show_in_all_projectsに反映
      if (oldProject.is_team_project && 'show_in_all_projects' in updates) {
        (updatedProject as any).user_show_in_all_projects = updates.show_in_all_projects
      }
      set(state => ({
        projects: state.projects.map(p => p.id === id ? updatedProject : p),
        todos: state.todos.map(todo =>
          todo.project_id === id ? { ...todo, project: updatedProject } : todo
        ),
      }))
    }

    // デモモード中はDB更新をスキップ
    if (isDemoModeActive()) {
      return
    }

    set({ loading: true, error: null })
    try {
      if (!oldProject) {
        throw new Error('Project not found')
      }

      // チームプロジェクトのshow_in_all_projects更新の場合は、個人設定テーブルに保存
      if (oldProject.is_team_project && 'show_in_all_projects' in updates) {
        const { data: { user } } = await supabase.auth.getUser()
        const currentUserId = user?.id

        if (currentUserId) {
          // user_project_settingsテーブルにupsert
          const { error: settingError } = await supabase
            .from('user_project_settings')
            .upsert({
              user_id: currentUserId,
              project_id: id,
              show_in_all_projects: updates.show_in_all_projects,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id,project_id'
            })

          if (settingError) throw settingError

          // チームプロジェクトの場合はupdatesからshow_in_all_projectsを除外
          const { show_in_all_projects, ...otherUpdates } = updates

          // 他の更新がある場合のみprojectsテーブルを更新
          if (Object.keys(otherUpdates).length > 0) {
            const { error: updateError } = await supabase
              .from('projects')
              .update(otherUpdates)
              .eq('id', id)

            if (updateError) throw updateError
          }

          // ローカルステートを更新（user_show_in_all_projectsを更新）
          const updatedProject = {
            ...oldProject,
            ...otherUpdates,
            user_show_in_all_projects: updates.show_in_all_projects
          }

          set(state => ({
            projects: state.projects.map(p => p.id === id ? updatedProject : p),
            todos: state.todos.map(todo =>
              todo.project_id === id
                ? { ...todo, project: updatedProject }
                : todo
            ),
            loading: false,
            lastManualUpdateAt: Date.now()
          }))
        }
      } else {
        // 個人プロジェクトまたは他の更新の場合は通常通りprojectsテーブルを更新
        const { error: updateError } = await supabase
          .from('projects')
          .update(updates)
          .eq('id', id)

        if (updateError) throw updateError

        // ローカルステートを即座に更新（楽観的更新）
        const updatedProject = {
          ...oldProject,
          ...updates,
        }

        // 手動更新時刻を記録（リアルタイム同期スキップ用）
        set(state => ({
          projects: state.projects.map(p => p.id === id ? updatedProject : p),
          todos: state.todos.map(todo =>
            todo.project_id === id
              ? { ...todo, project: updatedProject }
              : todo
          ),
          loading: false,
          lastManualUpdateAt: Date.now()
        }))
      }

      // show_in_all_projectsが変更された場合、かつ「全てのプロジェクト」が選択されている場合は再取得
      if ('show_in_all_projects' in updates) {
        const { selectedProjectId } = get()
        if (selectedProjectId === null) {
          await get().fetchTodos()
          await get().fetchTodoStats()
        }
      }
    } catch (error) {
      const errorMessage = (error as Error).message

      // RLS policy violation for project updates
      if (errorMessage.includes('row-level security policy') || errorMessage.includes('violates')) {
        set({
          error: 'プロジェクトを編集できません。グレースピリオド中（8日目以降）は読み取り専用モードです。',
          loading: false
        })
      } else {
        set({ error: errorMessage, loading: false })
      }
    }
  },

  updateProjectOrder: async (id, newOrder) => {
    // display_order変更前にタイムスタンプを更新（Realtime syncをスキップさせる）
    set({ lastManualUpdateAt: Date.now() })

    // 楽観的更新（即座に UI を更新）＋ソート
    set(state => ({
      projects: state.projects
        .map(p => p.id === id ? { ...p, display_order: newOrder } : p)
        .sort((a, b) => a.display_order - b.display_order)
    }))

    // デモモード中はDB呼び出しをスキップ（楽観的更新のみ）
    if (isDemoModeActive()) {
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      const { error } = await supabase
        .from('projects')
        .update({ display_order: newOrder })
        .eq('id', id)
        .eq('user_id', user.id)

      if (error) throw error

      // 更新成功後にもタイムスタンプを更新
      set({ lastManualUpdateAt: Date.now() })
    } catch (error) {
      console.error('updateProjectOrder error:', error)
      // エラー時は再取得して正しい順序に戻す
      get().fetchProjects()
    }
  },

  deleteProject: async (id) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      set(state => ({
        projects: state.projects.filter(p => p.id !== id),
        todos: state.todos.filter(t => t.project_id !== id),
        selectedProjectId: state.selectedProjectId === id ? null : state.selectedProjectId,
        loading: false
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  addTodo: async (todo) => {
    // デモモード中はメモリ内でタスクを追加
    if (isDemoModeActive()) {
      const { todos, projects } = get()

      // 最小display_orderを取得
      const minOrder = todos.length > 0
        ? Math.min(...todos.map(t => t.display_order))
        : 0
      const newOrder = minOrder - 1

      // 新しいタスクを作成
      const newTodo = {
        ...todo,
        id: `demo-todo-${Date.now()}-${Math.random().toString(36).substring(7)}`,
        user_id: 'demo-user-001',
        display_order: newOrder,
        gantt_sort_order: newOrder,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        completed_at: null,
        deleted_at: null,
        thumbnail_image_id: null,
        thumbnail_size: 'small' as const,
        assigned_to_user_id: null,
        assignment_status: 'unassigned' as const,
        progress_percentage: 0,
        project: projects.find(p => p.id === todo.project_id) || null,
      }

      set(state => ({
        todos: [newTodo as any, ...state.todos]
      }))
      return
    }

    set({ loading: true, error: null })
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      // 全てのタスクの中から最小display_orderを取得（Supabaseから直接取得）
      const { data: minOrderData, error: minOrderError } = await supabase
        .from('todos')
        .select('display_order')
        .order('display_order', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (minOrderError && minOrderError.code !== 'PGRST116') {
        // PGRST116 = データが見つからない（タスクが1つもない場合）
        throw minOrderError
      }

      // 新しいタスクは全タスクの最小値-1を設定（一番上に表示）
      const minOrder = minOrderData?.display_order ?? 0
      const newOrder = minOrder - 1

      const { error } = await supabase
        .from('todos')
        .insert([{ ...todo, user_id: user.id, display_order: newOrder }])
        .select('*, project:projects(*)')
        .single()

      if (error) throw error

      // タスク数を更新
      const { selectedProjectId } = get()
      await get().fetchTodoStats(selectedProjectId ?? undefined)

      // タスクリストを再取得して、正しいフィルタリングを適用
      await get().fetchTodos(selectedProjectId ?? undefined)

      set({ loading: false })
    } catch (error) {
      const errorMessage = (error as Error).message

      // RLS policy violation for todos
      if (errorMessage.includes('row-level security policy') || errorMessage.includes('violates') || errorMessage.includes('new row violates')) {
        // ユーザーのプラン情報を取得してメッセージをカスタマイズ
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('subscription_tier')
            .eq('id', user.id)
            .single()

          const { data: configData } = await supabase
            .from('admin_tier_config')
            .select('max_todos_per_project')
            .eq('tier_name', profileData?.subscription_tier || 'free')
            .eq('is_active', true)
            .single()

          const maxTodos = configData?.max_todos_per_project || 20
          const tierName = profileData?.subscription_tier === 'free' ? 'Freeプラン' :
                          profileData?.subscription_tier === 'pro' ? 'Proプラン' :
                          profileData?.subscription_tier === 'premium' ? 'Premiumプラン' : 'AIアシスタントプラン'

          set({
            error: `タスクを作成できません。${tierName}では1プロジェクトあたり${maxTodos}個までしか作成できません。既存のタスクを削除するか、プランをアップグレードしてください。`,
            loading: false
          })
        } else {
          set({
            error: 'タスクの作成上限に達しました。既存のタスクを削除するか、プランをアップグレードしてください。',
            loading: false
          })
        }
      } else {
        set({ error: errorMessage, loading: false })
      }
    }
  },

  updateTodo: async (id, updates) => {
    // 楽観的更新（即座にUIを更新）
    set(state => ({
      todos: state.todos.map(t => t.id === id ? { ...t, ...updates } : t),
    }))

    // デモモード中はDB更新をスキップ
    if (isDemoModeActive()) {
      return
    }

    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('todos')
        .update(updates)
        .eq('id', id)

      if (error) {
        console.error('Supabase update error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          fullError: error
        })
        throw error
      }

      console.log('Update successful - todo ID:', id, 'updates:', updates)

      set({ loading: false })

      // タスク数を更新（ステータスが変更された可能性があるため）
      const { selectedProjectId } = get()
      await get().fetchTodoStats(selectedProjectId ?? undefined)
    } catch (error) {
      const errorMessage = (error as Error).message

      // RLS policy violation for todo updates
      if (errorMessage.includes('row-level security policy') || errorMessage.includes('violates')) {
        set({
          error: 'タスクを編集できません。グレースピリオド中（8日目以降）は読み取り専用モードです。',
          loading: false
        })
      } else {
        set({ error: errorMessage, loading: false })
      }
    }
  },

  updateTodoOrder: async (id, newOrder) => {
    // display_order変更前にタイムスタンプを更新（Realtime syncをスキップさせる）
    set({ lastManualUpdateAt: Date.now() })

    // 楽観的更新: 即座にUIを更新
    set(state => ({
      todos: state.todos.map(t =>
        t.id === id ? { ...t, display_order: newOrder } : t
      )
    }))

    // デモモード中はDB更新をスキップ（楽観的更新のみ）
    if (isDemoModeActive()) {
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      const { error } = await supabase.rpc('update_todo_order', {
        p_todo_id: id,
        p_new_order: newOrder,
        p_user_id: user.id
      })

      if (error) throw error
      // 成功時は楽観的更新がそのまま残る（再取得不要）
    } catch (error) {
      // エラー時は再取得して元に戻す
      const { selectedProjectId } = get()
      await get().fetchTodos(selectedProjectId ?? undefined)
      set({ error: (error as Error).message })
    }
  },

  updateTodosOrderBatch: async (todoIds, newOrders) => {
    // display_order変更前にタイムスタンプを更新（Realtime syncをスキップさせる）
    set({ lastManualUpdateAt: Date.now() })

    // 楽観的更新: 即座にUIを更新
    set(state => ({
      todos: state.todos.map(t => {
        const index = todoIds.indexOf(t.id)
        if (index !== -1) {
          return { ...t, display_order: newOrders[index] }
        }
        return t
      })
    }))

    // デモモード中はDB更新をスキップ（楽観的更新のみ）
    if (isDemoModeActive()) {
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      // 個別更新（バッチRPCがないため）
      for (let i = 0; i < todoIds.length; i++) {
        const { error } = await supabase.rpc('update_todo_order', {
          p_todo_id: todoIds[i],
          p_new_order: newOrders[i],
          p_user_id: user.id
        })
        if (error) {
          console.error('[updateTodosOrderBatch] Error updating todo:', todoIds[i], error)
        }
      }
      console.log('[updateTodosOrderBatch] Success - updated', todoIds.length, 'tasks')
    } catch (error) {
      console.error('[updateTodosOrderBatch] Error:', error)
      // エラー時は再取得して元に戻す
      const { selectedProjectId } = get()
      await get().fetchTodos(selectedProjectId ?? undefined)
      set({ error: (error as Error).message })
    }
  },

  updateGanttTodosOrder: async (todoIds, newOrders) => {
    // gantt_sort_order変更前にタイムスタンプを更新（Realtime syncをスキップさせる）
    set({ lastManualUpdateAt: Date.now() })

    // 保留中の順序を記録（fetchTodos中に再適用するため）
    const newPendingOrders = new Map(get().pendingGanttOrders)
    todoIds.forEach((id, index) => {
      newPendingOrders.set(id, newOrders[index])
    })
    set({ pendingGanttOrders: newPendingOrders })

    // 楽観的更新: 即座にUIを更新
    set(state => ({
      todos: state.todos.map(t => {
        const index = todoIds.indexOf(t.id)
        if (index !== -1) {
          return { ...t, gantt_sort_order: newOrders[index] }
        }
        return t
      })
    }))

    // デモモード中はDB更新をスキップ（楽観的更新のみ）
    if (isDemoModeActive()) {
      // 成功時は保留中の順序をクリア
      const updatedPending = new Map(get().pendingGanttOrders)
      todoIds.forEach(id => updatedPending.delete(id))
      set({ pendingGanttOrders: updatedPending })
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      // DBのバッチ更新関数を呼び出し
      const { error } = await supabase.rpc('update_gantt_todos_order_batch', {
        p_todo_ids: todoIds,
        p_new_orders: newOrders,
        p_user_id: user.id
      })

      if (error) {
        console.error('[updateGanttTodosOrder] RPC error:', error)
        // RPC関数が存在しない場合は個別更新にフォールバック
        if (error.message?.includes('function') || error.code === '42883') {
          console.log('[updateGanttTodosOrder] Falling back to individual updates')
          for (let i = 0; i < todoIds.length; i++) {
            const { error: updateError } = await supabase
              .from('todos')
              .update({ gantt_sort_order: newOrders[i] })
              .eq('id', todoIds[i])
              .eq('user_id', user.id)
            if (updateError) {
              console.error('[updateGanttTodosOrder] Individual update error:', updateError)
            }
          }
        } else {
          throw error
        }
      }
      console.log('[updateGanttTodosOrder] Success - updated', todoIds.length, 'tasks')
      // 成功時は保留中の順序をクリア
      const updatedPending = new Map(get().pendingGanttOrders)
      todoIds.forEach(id => updatedPending.delete(id))
      set({ pendingGanttOrders: updatedPending })
    } catch (error) {
      console.error('[updateGanttTodosOrder] Error:', error)
      // エラー時は保留中の順序をクリアして再取得
      const updatedPending = new Map(get().pendingGanttOrders)
      todoIds.forEach(id => updatedPending.delete(id))
      set({ pendingGanttOrders: updatedPending })
      const { selectedProjectId } = get()
      await get().fetchTodos(selectedProjectId ?? undefined)
      set({ error: (error as Error).message })
    }
  },

  // タスク画面の順序（display_order）をガント（gantt_sort_order）に同期
  syncGanttOrderFromTaskOrder: async (projectId) => {
    set({ lastManualUpdateAt: Date.now() })

    // 該当プロジェクトのタスクを取得
    const projectTodos = get().todos.filter(t => t.project_id === projectId)
    if (projectTodos.length === 0) return

    // 楽観的更新: display_orderをgantt_sort_orderにコピー
    set(state => ({
      todos: state.todos.map(t =>
        t.project_id === projectId
          ? { ...t, gantt_sort_order: t.display_order }
          : t
      )
    }))

    // デモモード中はDB更新をスキップ
    if (isDemoModeActive()) {
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      // DBを一括更新
      const updates = projectTodos.map(t => ({
        id: t.id,
        gantt_sort_order: t.display_order
      }))

      for (const update of updates) {
        const { error } = await supabase
          .from('todos')
          .update({ gantt_sort_order: update.gantt_sort_order })
          .eq('id', update.id)
          .eq('user_id', user.id)
        if (error) {
          console.error('[syncGanttOrderFromTaskOrder] Update error:', error)
        }
      }

      console.log('[syncGanttOrderFromTaskOrder] Success - synced', updates.length, 'tasks')
    } catch (error) {
      console.error('[syncGanttOrderFromTaskOrder] Error:', error)
      const { selectedProjectId } = get()
      await get().fetchTodos(selectedProjectId ?? undefined)
      set({ error: (error as Error).message })
    }
  },

  // ガントの順序（gantt_sort_order）をタスク画面（display_order）に同期
  syncTaskOrderFromGanttOrder: async (projectId) => {
    set({ lastManualUpdateAt: Date.now() })

    // 該当プロジェクトのタスクを取得
    const projectTodos = get().todos.filter(t => t.project_id === projectId)
    if (projectTodos.length === 0) return

    // 楽観的更新: gantt_sort_orderをdisplay_orderにコピー
    set(state => ({
      todos: state.todos.map(t =>
        t.project_id === projectId && t.gantt_sort_order !== null
          ? { ...t, display_order: t.gantt_sort_order }
          : t
      )
    }))

    // デモモード中はDB更新をスキップ
    if (isDemoModeActive()) {
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('ユーザーが認証されていません')

      // DBを一括更新
      const updates = projectTodos
        .filter(t => t.gantt_sort_order !== null)
        .map(t => ({
          id: t.id,
          display_order: t.gantt_sort_order!
        }))

      for (const update of updates) {
        const { error } = await supabase
          .from('todos')
          .update({ display_order: update.display_order })
          .eq('id', update.id)
          .eq('user_id', user.id)
        if (error) {
          console.error('[syncTaskOrderFromGanttOrder] Update error:', error)
        }
      }

      console.log('[syncTaskOrderFromGanttOrder] Success - synced', updates.length, 'tasks')
    } catch (error) {
      console.error('[syncTaskOrderFromGanttOrder] Error:', error)
      const { selectedProjectId } = get()
      await get().fetchTodos(selectedProjectId ?? undefined)
      set({ error: (error as Error).message })
    }
  },

  deleteTodo: async (id) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      set(state => ({
        todos: state.todos.filter(t => t.id !== id),
        loading: false
      }))

      // タスク数を更新
      const { selectedProjectId } = get()
      await get().fetchTodoStats(selectedProjectId ?? undefined)
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  setSelectedProjectId: async (id) => {
    set({ selectedProjectId: id })
    // 即座にTodoを再取得（完了を待つ）
    if (id) {
      await get().fetchTodos(id)
      await get().fetchTodoStats(id)
    } else {
      await get().fetchTodos()
      await get().fetchTodoStats()
    }
  },

  setIsOffline: (offline) => set({ isOffline: offline }),

  setIsProjectSelectorOpen: (isOpen) => set({ isProjectSelectorOpen: isOpen }),

  setSwipedTodoId: (id) => set({ swipedTodoId: id }),

  setGanttFullscreenMode: (mode) => set({ ganttFullscreenMode: mode }),
  setGanttDayWidth: (width) => set({ ganttDayWidth: width }),
  setGanttCenterDayIndex: (index) => set({ ganttCenterDayIndex: index }),
  setGanttSelectedTeamId: (teamId) => set({ ganttSelectedTeamId: teamId }),
  setActiveTab: (tab) => set({ activeTab: tab }),

  setIsPersonalSectionVisible: (visible) => {
    localStorage.setItem('personal_section_visible', JSON.stringify(visible))
    set({ isPersonalSectionVisible: visible })
  },

  setIsTeamSectionVisible: (visible) => {
    localStorage.setItem('team_section_visible', JSON.stringify(visible))
    set({ isTeamSectionVisible: visible })
  },

  notifyTodoFilesChanged: () => set({ todoFilesChangedAt: Date.now() }),

  clearError: () => set({ error: null }),

  clearStore: () => set({
    projects: [],
    todos: [],
    selectedProjectId: null,
    loading: false,
    error: null,
    isOffline: false,
    isProjectSelectorOpen: false,
    swipedTodoId: null,
    todoFilesChangedAt: 0,
    lastManualUpdateAt: 0,
    pendingGanttOrders: new Map(),
    ganttFullscreenMode: 'none',
    ganttSelectedTeamId: null,
    activeTab: 'tasks',
    hasMoreTodos: true,
    loadingMore: false,
    currentPage: 0,
    totalTodoCount: 0,
    todoCountsByStatus: {
      pending: 0,
      in_progress: 0,
      completed: 0,
      deleted: 0,
      total: 0
    }
  }),

  getLastManualUpdateAt: () => get().lastManualUpdateAt,

  // デモモード用のセッター（本番DBには一切影響しない）
  setProjects: (projects: Project[]) => set({ projects }),
  setTodos: (todos: Todo[]) => set({ todos }),
  setSelectedProjectIdForDemo: (id: string | null) => set({ selectedProjectId: id })  // DB呼び出しなし
}),
    {
      name: 'tastack-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedProjectId: state.selectedProjectId
      })
    }
  )
)